'use client'

import * as React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  IconArrowUpRight,
  IconChecklist,
  IconDeviceDesktop,
  IconLifebuoy,
  IconRoute,
  IconServer,
  IconTag,
  IconUserCircle,
} from '@tabler/icons-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { BrandMark } from '@/components/ui/brand-mark'
import { branding } from '@/lib/branding.config'
import { cn } from '@/lib/utils'
import { CommandCenter } from './command-center'
import { AvatarFace, CreditCells, creditLevel } from './dock-parts'

export interface NavbarItem {
  label: string
  href: string
  icon?: React.ReactNode
}

export interface NavbarProps {
  logoHref?: string
  navigationLinks?: NavbarItem[]
  showWallet?: boolean
  walletBalance?: string
  walletCurrency?: string
  /** False until the balance has been fetched: the store starts at a placeholder zero. */
  walletReady?: boolean
  walletHref?: string
  showAuth?: boolean
  isAuthenticated?: boolean
  user?: {
    name?: string
    email?: string
    image?: string
  }
  /** Safe mode, decided on the server: machines in the account panel are simulated. */
  testMode?: boolean
  onAccountOpen?: () => void
  onSignOut?: () => void | Promise<void>
  signInHref?: string
  className?: string
}

const DEFAULT_NAV_LINKS: NavbarItem[] = [
  { label: 'Cloud PCs', href: '/products' },
  { label: 'My Machines', href: '/machines' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Requirements', href: '/requirements' },
  { label: 'Help', href: '/help' },
]

// Quick-menu tiles on small screens: an icon and a hue per destination.
const NAV_ICONS: Record<string, typeof IconArrowUpRight> = {
  '/products': IconDeviceDesktop,
  '/machines': IconServer,
  '/how-it-works': IconRoute,
  '/pricing': IconTag,
  '/requirements': IconChecklist,
  '/help': IconLifebuoy,
}
const TILE_HUES = ['var(--primary)', 'var(--accent)', 'var(--chart-3)', 'var(--chart-2)', 'var(--accent)', 'var(--primary)']

type TabRect = { x: number; w: number }

export function NavbarTwoRow({
  logoHref = '/',
  navigationLinks = DEFAULT_NAV_LINKS,
  showWallet = false,
  walletBalance = '€0.00',
  walletCurrency = 'EUR',
  walletReady = true,
  walletHref = '/wallet',
  showAuth = true,
  isAuthenticated = false,
  user,
  testMode = false,
  onAccountOpen,
  onSignOut,
  signInHref = '/sign-in',
  className,
}: NavbarProps) {
  const pathname = usePathname()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [panelOpen, setPanelOpen] = React.useState(false)
  const [scrolled, setScrolled] = React.useState(false)
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null)
  const [rects, setRects] = React.useState<TabRect[]>([])
  const tabsRef = React.useRef<HTMLDivElement>(null)
  const tabRefs = React.useRef<(HTMLAnchorElement | null)[]>([])
  // Set while the quick menu hands over to the command center, so the menu
  // doesn't pull focus back to its trigger.
  const handingOver = React.useRef(false)

  const isMatch = (href: string) =>
    href === '/' ? pathname === '/' : pathname === href || !!pathname?.startsWith(`${href}/`)

  // The longest matching link wins, so a nested route lights one tab only.
  const activeIndex = navigationLinks.reduce(
    (best, link, index) =>
      isMatch(link.href) && (best === -1 || link.href.length > navigationLinks[best].href.length) ? index : best,
    -1
  )

  // Close both overlays whenever the route changes.
  React.useEffect(() => {
    setMenuOpen(false)
    setPanelOpen(false)
  }, [pathname])

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Measure the tabs so the plates can slide between them.
  React.useLayoutEffect(() => {
    const wrap = tabsRef.current
    if (!wrap) return
    const measure = () =>
      setRects(tabRefs.current.map((el) => (el ? { x: el.offsetLeft, w: el.offsetWidth } : { x: 0, w: 0 })))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(wrap)
    document.fonts?.ready.then(measure).catch(() => {})
    return () => observer.disconnect()
  }, [navigationLinks])

  const ready = rects.length === navigationLinks.length && rects.some((r) => r.w > 0)
  const activeRect = activeIndex >= 0 ? rects[activeIndex] : undefined
  const hoverRect = hoverIndex !== null && hoverIndex !== activeIndex ? rects[hoverIndex] : undefined
  const level = creditLevel(walletBalance)
  const account = showAuth && isAuthenticated ? user : undefined

  const openPanel = () => {
    onAccountOpen?.()
    setPanelOpen(true)
  }

  return (
    <nav
      aria-label="Main"
      data-scrolled={scrolled}
      className={cn('pointer-events-none sticky top-0 z-50 w-full px-2.5 pt-2.5 sm:px-5 sm:pt-4', className)}
    >
      <div className="mx-auto flex max-w-[1376px] items-center gap-2 sm:gap-3">
        {/* 1 · Brand module */}
        <Link
          href={logoHref}
          aria-label={`${branding.name} home`}
          className="group pointer-events-auto shrink-0 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
        >
          <span className="dock-mod flex h-14 items-center gap-3 pl-2 pr-2 sm:pr-4 lg:pr-2 xl:pr-4">
            <span className="dock-cube">
              <BrandMark size="sm" showText={false} />
              <span aria-hidden className="dock-cube-sheen" />
              {(['tl', 'tr', 'bl', 'br'] as const).map((pos, i) => (
                <span
                  key={pos}
                  aria-hidden
                  data-pos={pos}
                  className="dock-bracket"
                  style={{ '--delay': `${i * 400}ms` } as React.CSSProperties}
                />
              ))}
            </span>
            <span className="hidden flex-col leading-none sm:flex lg:hidden xl:flex">
              <span className="font-heading text-[15px] font-bold text-foreground">{branding.name}</span>
              <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                Cloud console
              </span>
            </span>
            <span aria-hidden data-pos="tr" className="dock-tick" />
            <span aria-hidden data-pos="bl" className="dock-tick" />
          </span>
        </Link>

        {/* 2 · Navigation module */}
        <div className="hidden min-w-0 flex-1 justify-center lg:flex">
          <div className="dock-mod pointer-events-auto flex h-14 items-center px-1.5">
            <div
              ref={tabsRef}
              className="dock-tabs relative"
              data-ready={ready || undefined}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <span
                aria-hidden
                data-kind="hover"
                className="dock-plate"
                style={
                  {
                    '--x': `${hoverRect?.x ?? activeRect?.x ?? 0}px`,
                    '--w': `${hoverRect?.w ?? activeRect?.w ?? 0}px`,
                    opacity: hoverRect ? 1 : 0,
                  } as React.CSSProperties
                }
              >
                <span className="dock-plate-face" />
              </span>
              {ready && activeRect && (
                <span
                  aria-hidden
                  data-kind="active"
                  className="dock-plate"
                  style={{ '--x': `${activeRect.x}px`, '--w': `${activeRect.w}px` } as React.CSSProperties}
                >
                  <span className="dock-plate-glow" />
                  <span className="dock-plate-face" />
                  <span key={activeIndex} className="dock-rail">
                    <span />
                    <span />
                    <span />
                  </span>
                </span>
              )}
              <ul className="flex items-center">
                {navigationLinks.map((link, index) => (
                  <li key={link.href}>
                    <Link
                      ref={(el) => {
                        tabRefs.current[index] = el
                      }}
                      href={link.href}
                      aria-current={index === activeIndex ? 'page' : undefined}
                      onMouseEnter={() => setHoverIndex(index)}
                      onFocus={() => setHoverIndex(index)}
                      onBlur={() => setHoverIndex(null)}
                      className="dock-tab"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <span aria-hidden data-pos="tr" className="dock-tick" />
            <span aria-hidden data-pos="bl" className="dock-tick" />
          </div>
        </div>

        <div aria-hidden className="flex-1 lg:hidden" />

        {/* 3 · Account module */}
        <div className="dock-mod pointer-events-auto flex h-14 shrink-0 items-center gap-1.5 px-2 sm:gap-2">
          {account ? (
            <>
              {showWallet && (
                <Link
                  href={walletHref}
                  className="dock-wallet"
                  aria-label={walletReady ? `Wallet balance ${walletBalance}` : 'Wallet'}
                >
                  <span aria-hidden className="dock-led" />
                  <span className="hidden sm:contents">
                    <CreditCells level={level} ready={walletReady} />
                  </span>
                  {walletReady ? (
                    <span className="font-mono text-[13px] font-semibold tabular-nums text-foreground">
                      {walletBalance}
                    </span>
                  ) : (
                    <span aria-hidden className="block h-3.5 w-14 animate-pulse rounded-sm bg-foreground/10" />
                  )}
                </Link>
              )}
              <span aria-hidden className="mx-0.5 hidden h-6 w-px bg-foreground/10 sm:block" />
              <button
                type="button"
                onClick={openPanel}
                aria-label="Open account command center"
                aria-haspopup="dialog"
                aria-expanded={panelOpen}
                aria-controls="command-center"
                className="dock-avatar"
              >
                <AvatarFace user={account} />
                <span aria-hidden className="dock-avatar-led" />
              </button>
            </>
          ) : showAuth ? (
            <>
              <Link
                href={signInHref}
                className="rounded-md px-2.5 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground sm:px-3"
              >
                Sign in
              </Link>
              <span className="hidden sm:block">
                <Link href="/sign-up" className="dock-cta px-4">
                  Create account
                </Link>
              </span>
            </>
          ) : null}

          {/* Quick menu: phones and tablets */}
          <span className="lg:hidden">
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label={menuOpen ? 'Close menu' : 'Open menu'} className="dock-key">
                  <span aria-hidden className="dock-pad">
                    {Array.from({ length: 9 }, (_, i) => (
                      <span key={i} />
                    ))}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={14}
                collisionPadding={10}
                onCloseAutoFocus={(event) => {
                  if (handingOver.current) {
                    event.preventDefault()
                    handingOver.current = false
                  }
                }}
                className="dock-panel relative z-[70] w-[min(26rem,calc(100vw-1.25rem))] rounded-2xl border-0 p-3 text-foreground"
              >
                <span aria-hidden className="dock-panel-grid" />
                <div className="relative flex items-center gap-2.5 px-1 pb-3 pt-1">
                  <span aria-hidden className="dock-led" />
                  <span className="dock-section-label !text-foreground">Quick menu</span>
                </div>

                <div className="relative grid grid-cols-2 gap-2">
                  {navigationLinks.map((link, index) => {
                    const Icon = NAV_ICONS[link.href] ?? IconArrowUpRight
                    return (
                      <DropdownMenuItem key={link.href} asChild>
                        <Link
                          href={link.href}
                          aria-current={index === activeIndex ? 'page' : undefined}
                          className="dock-tile dock-rise cursor-pointer focus:text-foreground"
                          style={{ '--hue': TILE_HUES[index % TILE_HUES.length], '--i': index } as React.CSSProperties}
                        >
                          <span className="dock-tile-icon">
                            {link.icon ?? <Icon className="h-4 w-4 text-current" stroke={2} />}
                          </span>
                          <span className="font-heading text-sm font-semibold">{link.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    )
                  })}
                </div>

                <DropdownMenuSeparator className="relative my-3 bg-foreground/10" />

                <div className="relative grid gap-2">
                  {account ? (
                    <>
                      {showWallet && (
                        <DropdownMenuItem asChild>
                          <Link href={walletHref} className="dock-wallet w-full cursor-pointer justify-between">
                            <span className="flex items-center gap-2.5 text-sm text-foreground/80">
                              <span aria-hidden className="dock-led" />
                              Wallet
                            </span>
                            {walletReady ? (
                              <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                                {walletBalance}
                              </span>
                            ) : (
                              <span aria-label="Loading balance" className="block h-3.5 w-16 animate-pulse rounded-sm bg-foreground/10" />
                            )}
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onSelect={() => {
                          handingOver.current = true
                          window.requestAnimationFrame(openPanel)
                        }}
                        className="dock-ghost w-full cursor-pointer"
                      >
                        <IconUserCircle className="h-4 w-4 text-current" stroke={2} />
                        Account and billing
                      </DropdownMenuItem>
                    </>
                  ) : showAuth ? (
                    <div className="grid grid-cols-2 gap-2">
                      <DropdownMenuItem asChild>
                        <Link href={signInHref} className="dock-ghost cursor-pointer">
                          Sign in
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/sign-up" className="dock-cta cursor-pointer">
                          Create account
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  ) : null}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
          <span aria-hidden data-pos="tr" className="dock-tick" />
          <span aria-hidden data-pos="bl" className="dock-tick" />
        </div>
      </div>

      {/* Command center: account panel from the right */}
      {account && (
        <CommandCenter
          open={panelOpen}
          onOpenChange={setPanelOpen}
          user={account}
          pathname={pathname}
          showWallet={showWallet}
          walletBalance={walletBalance}
          walletCurrency={walletCurrency}
          walletReady={walletReady}
          walletHref={walletHref}
          testMode={testMode}
          onSignOut={onSignOut}
        />
      )}
    </nav>
  )
}
